// [PROPOSITION-1] + [PROPOSITION-2] Cartes de proposition — owner & customer
import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  Package, Clock, Loader2, X, CheckCircle, Tag, RefreshCw,
  ShoppingBag, ArrowRight, DollarSign, Smartphone, CreditCard, Handshake
} from "lucide-react";
import { toast } from "sonner";

/* ─── Helpers ─── */
function timeLeft(expiration) {
  if (!expiration) return null;
  const diff = new Date(expiration).getTime() - Date.now();
  if (diff <= 0) return null;
  const h = Math.floor(diff / 3600000);
  const m = Math.floor((diff % 3600000) / 60000);
  return h > 0 ? `${h}h${m > 0 ? m + "m" : ""}` : `${m}m`;
}

function imgUrl(img) {
  if (!img) return null;
  if (img.startsWith("data:") || img.startsWith("http")) return img;
  return `data:image/jpeg;base64,${img}`;
}

/* ═══════════════════════════════════════════════════════════════════
   OWNER-SIDE PROPOSITION CARD
   [PROPOSITION-1] + [PROPOSITION-2] Enhanced confirmation modal
   ═══════════════════════════════════════════════════════════════════ */
export function OwnerPropositionCard({ msg, conv, onConfirm, onNewProposition, accent, devise }) {
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [confirmLoading, setConfirmLoading] = useState(false);
  const [modePaiement, setModePaiement] = useState("en_attente");

  const prop = msg.proposition || {};
  const produit = prop.produit || {};
  const statut = prop.statut || "en_attente";
  const time = new Date(msg.timestamp).toLocaleTimeString("fr-FR", { hour: "2-digit", minute: "2-digit" });
  const imgSrc = imgUrl(produit.image);
  const prixInit = produit.prixInitial || 0;
  const hasRemise = prixInit > prop.prixPropose;
  const remise = hasRemise ? prixInit - prop.prixPropose : 0;
  const total = (prop.prixPropose || 0) * (prop.quantite || 1);
  const deviseLabel = produit.devise || devise || "";

  const BADGES = {
    en_attente: { label: "En attente", color: "#F97316", bg: "rgba(249,115,22,0.12)" },
    acceptee: { label: "Acceptée", color: "#22C55E", bg: "rgba(34,197,94,0.12)" },
    refusee: { label: "Refusée", color: "#EF4444", bg: "rgba(239,68,68,0.12)" },
    confirmee: { label: "Confirmée", color: "#059669", bg: "rgba(5,150,105,0.12)" },
    expiree: { label: "Expirée", color: "#6B7280", bg: "rgba(107,114,128,0.12)" },
  };
  const badge = BADGES[statut] || BADGES.en_attente;

  const paiementOptions = [
    { id: "especes", label: "Espèces", icon: DollarSign },
    { id: "mobile_money", label: "Mobile Money", icon: Smartphone },
    { id: "carte", label: "Carte", icon: CreditCard },
    { id: "en_attente", label: "À définir", icon: Handshake },
  ];

  const handleConfirm = async () => {
    setConfirmLoading(true);
    try {
      await onConfirm(msg.id, modePaiement);
      toast.success("Commande créée ! Stock mis à jour automatiquement.", { duration: 4000 });
      setShowConfirmModal(false);
    } catch {
      /* parent handles error toast */
    } finally { setConfirmLoading(false); }
  };

  return (
    <>
      <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}
        data-testid={`proposition-card-${msg.id}`}
        style={{ display: "flex", justifyContent: "flex-end", margin: "8px 0" }}>
        <div style={{
          maxWidth: 300, borderRadius: "14px 14px 4px 14px", overflow: "hidden",
          background: "#1A1A1A", border: "1px solid #2A2A2A",
          boxShadow: "0 2px 12px rgba(0,0,0,0.3)",
        }}>
          {imgSrc ? (
            <img src={imgSrc} alt={produit.nom} style={{ width: "100%", height: 120, objectFit: "cover" }} />
          ) : (
            <div style={{ width: "100%", height: 70, background: `linear-gradient(135deg, ${accent}33, ${accent}11)`, display: "flex", alignItems: "center", justifyContent: "center" }}>
              <Package size={24} style={{ color: accent, opacity: 0.6 }} />
            </div>
          )}
          <div style={{ padding: 12 }}>
            <span style={{ display: "inline-block", padding: "3px 10px", borderRadius: 20, background: badge.bg, color: badge.color, fontSize: 11, fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.3, marginBottom: 8 }}>
              {badge.label}
            </span>
            <div style={{ color: "#fff", fontWeight: 700, fontSize: 14 }}>{produit.nom}</div>
            <div style={{ color: "#6B7280", fontSize: 12, marginTop: 2 }}>
              {produit.taille && `Taille ${produit.taille}`}{produit.couleur && ` · ${produit.couleur}`}{prop.quantite > 1 && ` · x${prop.quantite}`}
            </div>
            {hasRemise && <div style={{ color: "#6B7280", fontSize: 12, textDecoration: "line-through", marginTop: 6 }}>{prixInit.toLocaleString("fr-FR")} {deviseLabel}</div>}
            <div style={{ display: "flex", alignItems: "baseline", gap: 4, marginTop: hasRemise ? 2 : 6 }}>
              <span style={{ color: "#F97316", fontWeight: 800, fontSize: 16 }}>{prop.prixPropose?.toLocaleString("fr-FR")}</span>
              <span style={{ color: "#6B7280", fontSize: 12 }}>{deviseLabel}</span>
            </div>
            {prop.note && <div style={{ color: "#9CA3AF", fontSize: 13, fontStyle: "italic", marginTop: 6 }}>{prop.note}</div>}
            {statut === "en_attente" && (() => { const tl = timeLeft(prop.dateExpiration); return tl ? <div style={{ color: "#F97316", fontSize: 11, marginTop: 6, display: "flex", alignItems: "center", gap: 4 }}><Clock size={10} />Expire dans {tl}</div> : null; })()}

            {/* [PROPOSITION-2] Confirm button → opens modal */}
            {statut === "acceptee" && (
              <button data-testid={`confirm-prop-${msg.id}`}
                onClick={() => setShowConfirmModal(true)}
                style={{ marginTop: 10, width: "100%", padding: 10, borderRadius: 8, background: "#22C55E", color: "#fff", border: "none", fontWeight: 700, fontSize: 13, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", gap: 6 }}>
                <CheckCircle size={14} /> Confirmer la commande
              </button>
            )}
            {statut === "refusee" && onNewProposition && (
              <button data-testid={`new-prop-${msg.id}`}
                onClick={() => onNewProposition(produit)}
                style={{ marginTop: 10, width: "100%", padding: 10, borderRadius: 8, background: "transparent", color: "#FF4500", border: "1px solid rgba(255,69,0,0.3)", fontWeight: 600, fontSize: 13, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", gap: 6 }}>
                <RefreshCw size={13} /> Nouvelle proposition
              </button>
            )}
            <div style={{ color: "#4B5563", fontSize: 10, marginTop: 8, textAlign: "right" }}>{time}</div>
          </div>
        </div>
      </motion.div>

      {/* [PROPOSITION-2] Confirmation modal — full recap */}
      <AnimatePresence>
        {showConfirmModal && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            onClick={() => setShowConfirmModal(false)}
            style={{ position: "fixed", inset: 0, zIndex: 1000, background: "rgba(0,0,0,0.7)", backdropFilter: "blur(8px)", display: "flex", alignItems: "center", justifyContent: "center", padding: 20 }}>
            <motion.div initial={{ scale: 0.92, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} exit={{ scale: 0.92 }}
              transition={{ type: "spring", damping: 24, stiffness: 300 }}
              onClick={e => e.stopPropagation()}
              data-testid="confirm-order-modal"
              style={{ background: "#1A1A1A", borderRadius: 20, padding: "28px 24px", width: "100%", maxWidth: 420, border: "1px solid #2A2A2A", boxShadow: "0 24px 64px rgba(0,0,0,0.5)", maxHeight: "85vh", overflow: "auto" }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 20 }}>
                <h3 style={{ color: "#fff", fontSize: 18, fontWeight: 700 }}>Confirmer la commande</h3>
                <button onClick={() => setShowConfirmModal(false)} style={{ background: "none", border: "none", cursor: "pointer", color: "#6B7280", padding: 4 }}><X size={18} /></button>
              </div>

              {/* Recap card */}
              <div style={{ background: "#0F0F0F", borderRadius: 12, padding: 16, marginBottom: 16, border: "1px solid rgba(255,255,255,0.04)" }}>
                <div style={{ color: "#9CA3AF", fontSize: 12, marginBottom: 10 }}>
                  Cliente : <span style={{ color: "#fff", fontWeight: 600 }}>{conv?.clientePrenom || "Cliente"}</span>
                </div>
                <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 12 }}>
                  {imgSrc ? (
                    <img src={imgSrc} alt="" style={{ width: 48, height: 48, borderRadius: 8, objectFit: "cover" }} />
                  ) : (
                    <div style={{ width: 48, height: 48, borderRadius: 8, background: "#2A2A2A", display: "flex", alignItems: "center", justifyContent: "center" }}><Package size={20} style={{ color: "#4B5563" }} /></div>
                  )}
                  <div>
                    <div style={{ color: "#fff", fontWeight: 700, fontSize: 14 }}>{produit.nom} — {produit.taille || ""}</div>
                    <div style={{ color: "#9CA3AF", fontSize: 12 }}>Quantité : {prop.quantite || 1}</div>
                  </div>
                </div>
                <div style={{ borderTop: "1px solid rgba(255,255,255,0.06)", paddingTop: 10, display: "flex", flexDirection: "column", gap: 4 }}>
                  {hasRemise && <div style={{ display: "flex", justifyContent: "space-between", color: "#6B7280", fontSize: 12 }}><span>Prix initial</span><span>{prixInit.toLocaleString("fr-FR")} {deviseLabel}</span></div>}
                  {hasRemise && <div style={{ display: "flex", justifyContent: "space-between", color: "#22C55E", fontSize: 12 }}><span>Remise accordée</span><span>-{(remise * (prop.quantite || 1)).toLocaleString("fr-FR")} {deviseLabel}</span></div>}
                  <div style={{ display: "flex", justifyContent: "space-between", color: "#fff", fontSize: 15, fontWeight: 800, marginTop: 4 }}><span>TOTAL</span><span style={{ color: "#F97316" }}>{total.toLocaleString("fr-FR")} {deviseLabel}</span></div>
                </div>
              </div>

              {/* Stock info */}
              <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 16, color: "#9CA3AF", fontSize: 13 }}>
                <Package size={14} />
                <span>Stock {produit.nom} {produit.taille} après confirmation</span>
              </div>

              {/* Mode de paiement */}
              <div style={{ marginBottom: 20 }}>
                <label style={{ color: "#9CA3AF", fontSize: 12, fontWeight: 600, display: "block", marginBottom: 8 }}>Mode de paiement</label>
                <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
                  {paiementOptions.map(o => {
                    const Icon = o.icon;
                    return (
                      <button key={o.id} data-testid={`paiement-${o.id}`}
                        onClick={() => setModePaiement(o.id)}
                        style={{
                          padding: "8px 14px", borderRadius: 8,
                          background: modePaiement === o.id ? accent : "rgba(255,255,255,0.04)",
                          border: modePaiement === o.id ? "none" : "1px solid #2A2A2A",
                          color: modePaiement === o.id ? "#fff" : "#D1D5DB",
                          fontSize: 12, fontWeight: 600, cursor: "pointer",
                          display: "flex", alignItems: "center", gap: 6, transition: "all 150ms",
                        }}>
                        <Icon size={14} />{o.label}
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Actions */}
              <button data-testid="confirm-order-submit" onClick={handleConfirm} disabled={confirmLoading}
                style={{
                  width: "100%", padding: 16, borderRadius: 12,
                  background: "linear-gradient(135deg, #22C55E, #16A34A)",
                  color: "#fff", border: "none", fontWeight: 700, fontSize: 14,
                  cursor: "pointer", transition: "all 200ms", opacity: confirmLoading ? 0.6 : 1,
                  display: "flex", alignItems: "center", justifyContent: "center", gap: 8,
                  boxShadow: "0 4px 16px rgba(34,197,94,0.3)",
                }}>
                {confirmLoading ? <><Loader2 size={16} className="animate-spin" /> Création en cours...</> : <><CheckCircle size={16} /> Confirmer et créer la commande</>}
              </button>
              <button onClick={() => setShowConfirmModal(false)}
                style={{ width: "100%", padding: 10, marginTop: 8, background: "none", border: "none", color: "#6B7280", fontSize: 13, cursor: "pointer" }}>
                Annuler
              </button>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}


/* ═══════════════════════════════════════════════════════════════════
   CUSTOMER-SIDE PROPOSITION CARD
   [PROPOSITION-2] Full rewrite per spec
   ═══════════════════════════════════════════════════════════════════ */
export function CustomerPropositionCard({ msg, onRespond, accent, userId }) {
  const [showAcceptModal, setShowAcceptModal] = useState(false);
  const [showRefuseDialog, setShowRefuseDialog] = useState(false);
  const [acceptMsg, setAcceptMsg] = useState("");
  const [loading, setLoading] = useState(null);

  const prop = msg.proposition || {};
  const produit = prop.produit || {};
  const statut = prop.statut || "en_attente";
  const time = new Date(msg.timestamp).toLocaleTimeString("fr-FR", { hour: "2-digit", minute: "2-digit" });
  const imgSrc = imgUrl(produit.image);
  const prixInit = produit.prixInitial || 0;
  const hasRemise = prixInit > prop.prixPropose;
  const remise = hasRemise ? prixInit - prop.prixPropose : 0;
  const remisePct = prixInit > 0 ? Math.round((remise / prixInit) * 100) : 0;
  const deviseLabel = produit.devise || "";
  const total = (prop.prixPropose || 0) * (prop.quantite || 1);

  const handleAccept = async () => {
    setLoading("acceptee");
    try {
      await onRespond(msg.id, "acceptee", acceptMsg || null);
      toast.success("Proposition acceptée ! La gérante va confirmer votre commande.", { duration: 4000 });
      setShowAcceptModal(false);
    } catch { /* parent handles */ }
    finally { setLoading(null); }
  };

  const handleRefuse = async () => {
    setLoading("refusee");
    try {
      await onRespond(msg.id, "refusee", null);
      toast("Proposition refusée. Vous pouvez continuer à discuter avec la boutique.", { duration: 3000 });
      setShowRefuseDialog(false);
    } catch { /* parent handles */ }
    finally { setLoading(null); }
  };

  // Common card wrapper styles per status
  const isInactive = statut === "refusee" || statut === "expiree";
  const isConfirmed = statut === "confirmee";
  const isAccepted = statut === "acceptee";
  const cardBg = isConfirmed ? "rgba(6,78,59,0.2)" : isAccepted ? "rgba(6,78,59,0.15)" : "#1A1A1A";
  const cardBorder = isConfirmed ? "1px solid rgba(34,197,94,0.4)" : isAccepted ? "1px solid rgba(34,197,94,0.3)" : `1px solid ${accent}4D`;

  return (
    <>
      <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}
        data-testid={`proposition-card-${msg.id}`}
        style={{ display: "flex", justifyContent: "flex-start", margin: "8px 0", opacity: isInactive ? (statut === "expiree" ? 0.5 : 0.6) : 1 }}>
        <div style={{ maxWidth: 300, borderRadius: "14px 14px 14px 4px", overflow: "hidden", background: cardBg, border: cardBorder, boxShadow: "0 2px 12px rgba(0,0,0,0.3)" }}>
          {/* Image */}
          {imgSrc && !isInactive ? (
            <img src={imgSrc} alt={produit.nom} style={{ width: "100%", height: 120, objectFit: "cover" }} />
          ) : imgSrc ? (
            <img src={imgSrc} alt={produit.nom} style={{ width: "100%", height: 80, objectFit: "cover", filter: "grayscale(0.6)" }} />
          ) : (
            <div style={{ width: "100%", height: 60, background: `linear-gradient(135deg, ${accent}22, ${accent}11)`, display: "flex", alignItems: "center", justifyContent: "center" }}>
              <Package size={20} style={{ color: accent, opacity: 0.4 }} />
            </div>
          )}

          <div style={{ padding: 12 }}>
            {/* Badge per status */}
            {statut === "en_attente" && (
              <span style={{ display: "inline-block", padding: "3px 10px", borderRadius: 20, background: `${accent}26`, color: accent, fontSize: 11, fontWeight: 700, textTransform: "uppercase", marginBottom: 8 }}>
                Proposition reçue
              </span>
            )}
            {statut === "acceptee" && (
              <span style={{ display: "inline-block", padding: "3px 10px", borderRadius: 20, background: "rgba(34,197,94,0.15)", color: "#22C55E", fontSize: 11, fontWeight: 700, marginBottom: 8 }}>
                Acceptée — En attente de confirmation
              </span>
            )}
            {statut === "confirmee" && (
              <span style={{ display: "inline-block", padding: "3px 10px", borderRadius: 20, background: "rgba(34,197,94,0.15)", color: "#22C55E", fontSize: 11, fontWeight: 800, marginBottom: 8 }}>
                Commande confirmée !
              </span>
            )}
            {statut === "refusee" && (
              <span style={{ display: "inline-block", padding: "3px 10px", borderRadius: 20, background: "rgba(239,68,68,0.12)", color: "#EF4444", fontSize: 11, fontWeight: 700, marginBottom: 8 }}>
                Refusée
              </span>
            )}
            {statut === "expiree" && (
              <span style={{ display: "inline-block", padding: "3px 10px", borderRadius: 20, background: "rgba(107,114,128,0.12)", color: "#6B7280", fontSize: 11, fontWeight: 700, marginBottom: 8 }}>
                Expirée
              </span>
            )}

            {/* Product info */}
            <div style={{ color: "#fff", fontWeight: 700, fontSize: 14 }}>{produit.nom}</div>
            <div style={{ color: "#9CA3AF", fontSize: 12, marginTop: 2 }}>
              {produit.taille && `Taille ${produit.taille}`}{prop.quantite > 1 && ` · x${prop.quantite}`}
            </div>

            {/* Pricing */}
            {hasRemise && !isInactive && (
              <div style={{ color: "#9CA3AF", fontSize: 12, textDecoration: "line-through", marginTop: 6 }}>
                Prix habituel : {prixInit.toLocaleString("fr-FR")} {deviseLabel}
              </div>
            )}
            <div style={{ marginTop: hasRemise && !isInactive ? 2 : 6 }}>
              {!isInactive && <span style={{ color: "#9CA3AF", fontSize: 11 }}>Votre prix : </span>}
              <span style={{ color: "#F97316", fontWeight: 800, fontSize: isInactive ? 14 : 18 }}>
                {(statut === "confirmee" ? total : prop.prixPropose)?.toLocaleString("fr-FR")}
              </span>
              <span style={{ color: "#9CA3AF", fontSize: 12, marginLeft: 4 }}>{deviseLabel}</span>
            </div>
            {hasRemise && !isInactive && (
              <div style={{ marginTop: 4, padding: "4px 10px", borderRadius: 6, background: "rgba(34,197,94,0.1)", display: "inline-block", color: "#22C55E", fontSize: 12, fontWeight: 600 }}>
                Remise de -{remise.toLocaleString("fr-FR")} (-{remisePct}%)
              </div>
            )}

            {/* Note */}
            {prop.note && !isInactive && (
              <div style={{ marginTop: 8, background: "#0F0F0F", borderRadius: 8, padding: "8px 12px", color: "#9CA3AF", fontSize: 13, fontStyle: "italic" }}>
                {prop.note}
              </div>
            )}

            {/* Expiration — en_attente only */}
            {statut === "en_attente" && (() => {
              const tl = timeLeft(prop.dateExpiration);
              return tl ? (
                <div style={{ marginTop: 8, background: "rgba(249,115,22,0.1)", borderRadius: 8, padding: "6px 10px", color: "#F97316", fontSize: 12, display: "flex", alignItems: "center", gap: 4 }}>
                  <Clock size={12} /> Offre valable encore {tl}
                </div>
              ) : null;
            })()}

            {/* Acceptée info */}
            {statut === "acceptee" && (
              <div style={{ color: "#9CA3AF", fontSize: 13, marginTop: 8, lineHeight: 1.4 }}>
                La gérante va confirmer votre commande. Vous recevrez une notification.
              </div>
            )}

            {/* Confirmée order details */}
            {statut === "confirmee" && prop.commandeId && (
              <div style={{ marginTop: 8 }}>
                <div style={{ color: "#9CA3AF", fontSize: 12 }}>
                  La gérante vous contactera pour le paiement et la remise de l'article.
                </div>
                {userId && (
                  <a href={`/vitrine/${userId}/espace/orders`}
                    data-testid="voir-commande-link"
                    style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 6, marginTop: 10, padding: "10px 0", borderRadius: 8, background: "rgba(34,197,94,0.15)", color: "#22C55E", fontSize: 13, fontWeight: 600, textDecoration: "none", cursor: "pointer" }}>
                    <ShoppingBag size={14} /> Voir ma commande <ArrowRight size={12} />
                  </a>
                )}
              </div>
            )}

            {/* Expirée info */}
            {statut === "expiree" && (
              <div style={{ color: "#6B7280", fontSize: 12, marginTop: 6 }}>
                Cette proposition n'est plus disponible.
              </div>
            )}

            {/* ── Buttons en_attente ── */}
            {statut === "en_attente" && (
              <div style={{ display: "flex", gap: 8, marginTop: 10 }}>
                <button data-testid={`refuse-prop-${msg.id}`}
                  onClick={() => setShowRefuseDialog(true)}
                  disabled={!!loading}
                  style={{ flex: 1, padding: 12, borderRadius: 10, background: "transparent", color: "#EF4444", border: "1px solid rgba(239,68,68,0.3)", fontWeight: 600, fontSize: 14, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", gap: 4 }}>
                  <X size={14} /> Refuser
                </button>
                <button data-testid={`accept-prop-${msg.id}`}
                  onClick={() => setShowAcceptModal(true)}
                  disabled={!!loading}
                  style={{ flex: 1, padding: 12, borderRadius: 10, background: "#22C55E", color: "#fff", border: "none", fontWeight: 700, fontSize: 14, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", gap: 4, boxShadow: "0 2px 8px rgba(34,197,94,0.3)" }}>
                  <CheckCircle size={14} /> J'accepte
                </button>
              </div>
            )}

            <div style={{ color: isInactive ? "#4B5563" : "#6B7280", fontSize: 10, marginTop: 8, textAlign: "right" }}>{time}</div>
          </div>
        </div>
      </motion.div>

      {/* ── [PROPOSITION-2] Accept modal ── */}
      <AnimatePresence>
        {showAcceptModal && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            onClick={() => setShowAcceptModal(false)}
            style={{ position: "fixed", inset: 0, zIndex: 1000, background: "rgba(0,0,0,0.7)", backdropFilter: "blur(8px)", display: "flex", alignItems: "center", justifyContent: "center", padding: 20 }}>
            <motion.div initial={{ scale: 0.92, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} exit={{ scale: 0.92 }}
              transition={{ type: "spring", damping: 24, stiffness: 300 }}
              onClick={e => e.stopPropagation()}
              data-testid="accept-proposition-modal"
              style={{ background: "#1A1A1A", borderRadius: 20, padding: "28px 24px", width: "100%", maxWidth: 380, border: "1px solid #2A2A2A", boxShadow: "0 24px 64px rgba(0,0,0,0.5)", maxHeight: "85vh", overflow: "auto" }}>

              <div style={{ textAlign: "center", marginBottom: 16 }}>
                <div style={{ width: 40, height: 40, borderRadius: "50%", margin: "0 auto 12px", background: "rgba(34,197,94,0.15)", display: "flex", alignItems: "center", justifyContent: "center" }}>
                  <CheckCircle size={22} style={{ color: "#22C55E" }} />
                </div>
                <h3 style={{ color: "#fff", fontSize: 18, fontWeight: 700 }}>Confirmer votre intérêt ?</h3>
                <p style={{ color: "#9CA3AF", fontSize: 14, marginTop: 6, lineHeight: 1.5 }}>
                  En acceptant cette proposition vous confirmez votre intérêt pour :
                </p>
              </div>

              {/* Recap */}
              <div style={{ background: "#0F0F0F", borderRadius: 12, padding: 16, marginBottom: 16, border: "1px solid rgba(255,255,255,0.04)" }}>
                <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                  {imgSrc ? (
                    <img src={imgSrc} alt="" style={{ width: 40, height: 40, borderRadius: 8, objectFit: "cover" }} />
                  ) : (
                    <div style={{ width: 40, height: 40, borderRadius: 8, background: "#2A2A2A", display: "flex", alignItems: "center", justifyContent: "center" }}><Package size={16} style={{ color: "#4B5563" }} /></div>
                  )}
                  <div>
                    <div style={{ color: "#fff", fontWeight: 700, fontSize: 14 }}>{produit.nom}</div>
                    <div style={{ color: "#9CA3AF", fontSize: 12 }}>Taille : {produit.taille || "—"}</div>
                    <div style={{ color: "#F97316", fontWeight: 800, fontSize: 15, marginTop: 2 }}>{prop.prixPropose?.toLocaleString("fr-FR")} {deviseLabel}</div>
                  </div>
                </div>
              </div>

              <p style={{ color: "#6B7280", fontSize: 13, textAlign: "center", marginBottom: 16, lineHeight: 1.5 }}>
                La gérante finalisera votre commande et vous recontactera pour le paiement et la livraison.
              </p>

              {/* Optional message */}
              <textarea
                data-testid="accept-message-input"
                value={acceptMsg}
                onChange={e => setAcceptMsg(e.target.value)}
                placeholder="Ajouter un message... Ex: Je peux récupérer samedi matin"
                rows={2}
                style={{ width: "100%", background: "#1A1A1A", border: "1px solid #2A2A2A", borderRadius: 10, padding: 12, color: "#fff", fontSize: 13, outline: "none", resize: "none", fontFamily: "inherit", marginBottom: 16 }}
              />

              <button data-testid="confirm-accept-btn" onClick={handleAccept} disabled={loading === "acceptee"}
                style={{ width: "100%", padding: 14, borderRadius: 12, background: `linear-gradient(135deg, ${accent}, ${accent}CC)`, color: "#fff", border: "none", fontWeight: 700, fontSize: 14, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", gap: 8, boxShadow: `0 4px 16px ${accent}33`, opacity: loading === "acceptee" ? 0.6 : 1 }}>
                {loading === "acceptee" ? <><Loader2 size={16} className="animate-spin" /> Envoi...</> : <>Confirmer mon intérêt <ArrowRight size={14} /></>}
              </button>
              <button onClick={() => setShowAcceptModal(false)} style={{ width: "100%", padding: 10, marginTop: 8, background: "none", border: "none", color: "#6B7280", cursor: "pointer", fontSize: 13 }}>Annuler</button>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* ── [PROPOSITION-2] Refuse dialog ── */}
      <AnimatePresence>
        {showRefuseDialog && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            onClick={() => setShowRefuseDialog(false)}
            style={{ position: "fixed", inset: 0, zIndex: 1000, background: "rgba(0,0,0,0.7)", backdropFilter: "blur(8px)", display: "flex", alignItems: "center", justifyContent: "center", padding: 20 }}>
            <motion.div initial={{ scale: 0.92, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} exit={{ scale: 0.92 }}
              onClick={e => e.stopPropagation()}
              data-testid="refuse-proposition-modal"
              style={{ background: "#1A1A1A", borderRadius: 16, padding: 24, width: "100%", maxWidth: 320, border: "1px solid #2A2A2A", textAlign: "center" }}>
              <p style={{ color: "#fff", fontSize: 16, fontWeight: 600, marginBottom: 20 }}>Refuser cette proposition ?</p>
              <div style={{ display: "flex", gap: 10 }}>
                <button onClick={() => setShowRefuseDialog(false)}
                  style={{ flex: 1, padding: 12, borderRadius: 10, background: "rgba(255,255,255,0.05)", color: "#9CA3AF", border: "1px solid #2A2A2A", cursor: "pointer", fontSize: 14, fontWeight: 600 }}>
                  Annuler
                </button>
                <button data-testid="confirm-refuse-btn" onClick={handleRefuse} disabled={loading === "refusee"}
                  style={{ flex: 1, padding: 12, borderRadius: 10, background: "rgba(239,68,68,0.1)", color: "#EF4444", border: "1px solid rgba(239,68,68,0.3)", cursor: "pointer", fontSize: 14, fontWeight: 700, opacity: loading === "refusee" ? 0.6 : 1 }}>
                  {loading === "refusee" ? <Loader2 size={14} className="animate-spin" style={{ margin: "0 auto" }} /> : "Oui, refuser"}
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
